import { DynamicForm, FormConfig } from "@components/DynamicForm/DynamicForm";
import { useNotification } from "@hooks/useNotification";
import { useQuery } from "@hooks/useQuery";
import { decodeHTML } from "@utils/CommonUtils";
import { useEffect } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useDocumentDetails } from "~/Modules/ECMS/MAGP/ApprovalManagement/Drilldown.Hooks";
import { post, put } from "~/Services";
import { eramanHost } from "~/Utils";
import { fields } from "../constants";
import { useAdsBrandDocumentDetails } from "./hooks/AdsBanner.hook";

export const AddEditAdsBrands = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const { openToast } = useNotification();

  // editId flow (DB/master data)
  const {
    data: brandData,
    isLoading: brandLoading,
    refetch: refetchBrands,
  } = useAdsBrandDocumentDetails(query.get("editId") || "");

  // id flow (draft/submitted document details)
  const { data: brandById, isLoading: docLoading, refetch: refetchDoc } = useDocumentDetails(query.get("id") || "");

  useEffect(() => {
    if (query.get("editId")) {
      refetchBrands();
    } else {
      // still fetch brand master data
      refetchBrands();
    }
    if (query.get("id")) {
      refetchDoc();
    }
  }, [query.get("editId"), query.get("id")]);

  const brandFromFields: FormConfig = {
    initialValues: {
      field_beauty_image: brandById?.payload
        ? decodeHTML([
            {
              uid: "-1",
              name: "field_beauty_image.png",
              status: "done",
              url: brandById?.payload?.field_beauty_image,
            },
          ])
        : decodeHTML([
            {
              uid: "-1",
              name: "field_beauty_image.png",
              status: "done",
              url: brandData?.field_beauty_image,
            },
          ]),

      field_confectionary_image: brandById?.payload
        ? decodeHTML([
            {
              uid: "-1",
              name: "field_confectionary_image.png",
              status: "done",
              url: brandById?.payload?.field_confectionary_image,
            },
          ])
        : decodeHTML([
            {
              uid: "-1",
              name: "field_confectionary_image.png",
              status: "done",
              url: brandData?.field_confectionary_image,
            },
          ]),

      field_wine_spirits_image: brandById?.payload
        ? decodeHTML([
            {
              uid: "-1",
              name: "field_wine_spirits_image.png",
              status: "done",
              url: brandById?.payload?.field_wine_spirits_image,
            },
          ])
        : decodeHTML([
            {
              uid: "-1",
              name: "field_wine_spirits_image.png",
              status: "done",
              url: brandData?.field_wine_spirits_image,
            },
          ]),

      field_english_title: decodeHTML(brandById?.payload?.field_english_title ?? brandData?.field_english_title ?? ""),
      field_malay_title: decodeHTML(brandById?.payload?.field_malay_title ?? brandData?.field_malay_title ?? ""),
      field_mandarin_title: decodeHTML(
        brandById?.payload?.field_mandarin_title ?? brandData?.field_mandarin_title ?? ""
      ),
      published: brandById?.payload?.published ?? brandData?.published ?? false,
    },
    fields: fields.bannnerBrands,
  };

  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    let imageKeys: any = ["field_beauty_image", "field_confectionary_image", "field_wine_spirits_image"];
    for (const key of imageKeys) {
      try {
        let field = data[key];
        if (field?.[0]?.originFileObj) {
          const formData = new FormData();
          formData.append("upload-file", field[0].originFileObj);
          formData.append("application", "ERAMAN");
          const res: any = await post("management/upload-file", formData);

          if (res) {
            data[key] = res.response.data;
          }
        } else if (field?.[0]?.url) {
          data[key] = field[0].url;
        } else {
          data[key] = ""; // fallback in case of invalid field
        }
      } catch (error) {
        console.error("Image processing failed for:", key, error);
      }
    }

    const payload = {
      action,
      method,
      module: "Advertisement",
      subModule: "bannnerBrands",
      type: "DEDICATED",
      application: "ERAMAN",
      reviewNotes,
      appList: [
        {
          appName: "ERAMAN",
          apiUrl,
        },
      ],
      payload: {
        ...(action === "UPDATE" && {
          id: brandData?.id || brandById?.payload?.id,
        }),
        ...data,
        status: true,
        published: data.published,
      },
      status,
    };

    console.log("Final Payload:", payload);

    if (query.get("id")) {
      // draft/submitted doc update flow
      put(`/management/update-document?approvalId=${query.get("id")}`, payload)
        .then((response: any) => {
          openToast({
            content: status == "PENDING" ? "Data Sent for Review" : "Data Saved Successfully",
            type: "success",
          });
          if (response.status === "OK" && status == "PENDING") {
            navigate("/dedicated/status-information?activeTab=pending");
          } else {
            navigate("/dedicated/status-information?activeTab=draft");
          }
        })
        .catch(() => {
          openToast({ content: "Failed to save data", type: "error" });
        });
    } else {
      // editId flow
      post("/management/publish", payload)
        .then((response: any) => {
          openToast({
            content: status == "DRAFT" ? "Data saved successfully" : "Data Sent for Review",
            type: "success",
          });
          if (response.status === "OK" && status == "PENDING") {
            navigate("/dedicated/status-information?activeTab=pending");
          } else {
            navigate("/dedicated/status-information?activeTab=draft");
          }
        })
        .catch(() => {
          openToast({ content: "Failed to save data", type: "error" });
        });
    }
  };

  return (
    <DynamicForm
      isLoading={brandLoading || docLoading}
      config={brandFromFields}
      onSubmit={(data, buttonValue, reviewNotes) =>
        onSubmit(
          data,
          `${eramanHost}/api/brands-products-banner-update-api/${brandData?.id || brandById?.payload?.id}`,
          query.get("editId") ? "UPDATE" : "UPDATE",
          query.get("editId") ? "PATCH" : "PATCH",
          buttonValue == "save" ? "DRAFT" : "PENDING",
          reviewNotes
        )
      }
    />
  );
};
